import { useCallback, useRef, useState } from 'react'
import RozmowaService from '@/services/RozmowaService'
import PlacowkaService from '@/services/PlacowkaService'

const PLACOWKI_BATCH_SIZE = 8
const PLACOWKI_SEARCH_LIMIT = 200
const ROZMOWY_SEARCH_LIMIT = 5000
const placowkiCache = new Map()
const placowkiRequests = new Map()

const getRelationId = relation => {
	if (!relation) return ''
	if (typeof relation === 'object') return relation?._id || relation?.id || ''
	return relation
}

const getPlacowkaValue = rozmowa => rozmowa?.id_placowka || rozmowa?.placowka

const getRozmowaId = rozmowa => rozmowa?._id || rozmowa?.id || ''

const hasPlacowkaLocationData = placowka => {
	if (!placowka || typeof placowka !== 'object') return false

	return !!(
		placowka?.id_miasto ||
		placowka?.id_gmina ||
		placowka?.id_powiat ||
		placowka?.id_wojewodztwo
	)
}

const savePlacowkaInCache = placowka => {
	const placowkaId = getRelationId(placowka)

	if (placowkaId && hasPlacowkaLocationData(placowka)) {
		placowkiCache.set(String(placowkaId), placowka)
	}
}

const savePlacowkiInCache = placowki => {
	placowki.forEach(placowka => savePlacowkaInCache(placowka))
}

const getPlacowkaDetails = async placowkaId => {
	const cacheKey = String(placowkaId)

	if (placowkiCache.has(cacheKey)) {
		return placowkiCache.get(cacheKey)
	}

	if (placowkiRequests.has(cacheKey)) {
		return placowkiRequests.get(cacheKey)
	}

	const request = PlacowkaService.getById(cacheKey)
		.then(result => {
			const placowka = result?.error ? null : result?.data || null
			placowkiCache.set(cacheKey, placowka)
			return placowka
		})
		.catch(() => {
			placowkiCache.set(cacheKey, null)
			return null
		})
		.finally(() => {
			placowkiRequests.delete(cacheKey)
		})

	placowkiRequests.set(cacheKey, request)
	return request
}

const loadPlacowkiDetails = async placowkaIds => {
	const ids = [...new Set(placowkaIds)].filter(Boolean)

	for (let index = 0; index < ids.length; index += PLACOWKI_BATCH_SIZE) {
		const batch = ids.slice(index, index + PLACOWKI_BATCH_SIZE)
		await Promise.all(batch.map(placowkaId => getPlacowkaDetails(placowkaId)))
	}
}

const getMergedPlacowka = (placowka, cachedPlacowka, placowkaId) => {
	if (!cachedPlacowka) return placowka

	const basePlacowka = placowka && typeof placowka === 'object' ? placowka : { _id: placowkaId }
	return { ...basePlacowka, ...cachedPlacowka }
}

const mergeRozmowyWithCachedPlacowki = rows => {
	return rows.map(rozmowa => {
		const placowka = getPlacowkaValue(rozmowa)
		const placowkaId = getRelationId(placowka)

		if (!placowkaId) return rozmowa

		const cachedPlacowka = placowkiCache.get(String(placowkaId))
		const mergedPlacowka = getMergedPlacowka(placowka, cachedPlacowka, placowkaId)

		if (!mergedPlacowka || mergedPlacowka === placowka) return rozmowa

		return {
			...rozmowa,
			id_placowka: mergedPlacowka,
		}
	})
}

const enrichRozmowyWithPlacowki = async rows => {
	const idsToLoad = []

	rows.forEach(rozmowa => {
		const placowka = getPlacowkaValue(rozmowa)
		const placowkaId = getRelationId(placowka)

		if (!placowkaId) return

		savePlacowkaInCache(placowka)

		if (!placowkiCache.has(String(placowkaId))) {
			idsToLoad.push(String(placowkaId))
		}
	})

	await loadPlacowkiDetails(idsToLoad)
	return mergeRozmowyWithCachedPlacowki(rows)
}

const getRozmowaFilters = filters => ({
	id_user: filters.id_user || null,
	id_placowka: filters.id_placowka || null,
	id_osoba: filters.id_osoba || null,
	call_time: filters.call_time || null,
	skipped: filters.skipped || null,
	created: filters.created || null,
	dig_from_date: filters.dig_from_date || null,
	dig_to_date: filters.dig_to_date || null,
	dig_nastepnej_rozmowy_from_date: filters.dig_nastepnej_rozmowy_from_date || null,
	dig_nastepnej_rozmowy_to_date: filters.dig_nastepnej_rozmowy_to_date || null,
	szansa:
		filters.szansa !== null &&
		filters.szansa !== undefined &&
		filters.szansa !== ''
			? filters.szansa
			: null,
})

const getPlacowkaSearchValue = placowka => [
	placowka?.nazwa,
	placowka?.adres,
	placowka?.id_miasto?.nazwa,
	placowka?.id_gmina?.nazwa,
	placowka?.id_powiat?.nazwa,
	placowka?.id_wojewodztwo?.nazwa,
].filter(Boolean).join(' ').toLowerCase()

const doesPlacowkaMatchSearchText = (placowka, searchText) => {
	const trimmed = (searchText || '').trim().toLowerCase()
	if (!trimmed) return true

	return getPlacowkaSearchValue(placowka).includes(trimmed)
}

const mergeRozmowyRows = (...rowGroups) => {
	const rowsMap = new Map()

	rowGroups.flat().forEach(rozmowa => {
		const rozmowaId = getRozmowaId(rozmowa)
		const mapKey = rozmowaId || `${rozmowa?.dig_rozmowy || ''}-${rozmowa?.feedback || ''}`

		if (!mapKey || rowsMap.has(String(mapKey))) return

		rowsMap.set(String(mapKey), rozmowa)
	})

	return [...rowsMap.values()]
}

const getSortValue = (row, sortColumn) => {
	const value = row?.[sortColumn]

	if (sortColumn === 'dig_rozmowy' || sortColumn === 'dig_nastepnej_rozmowy' || sortColumn === 'created') {
		return value ? new Date(value).getTime() : 0
	}

	return value ?? ''
}

const sortRozmowyRows = (rows, sortColumn, sortDirection) => {
	const direction = sortDirection === 'asc' ? 1 : -1

	return [...rows].sort((a, b) => {
		const firstValue = getSortValue(a, sortColumn)
		const secondValue = getSortValue(b, sortColumn)

		if (typeof firstValue === 'number' && typeof secondValue === 'number') {
			return (firstValue - secondValue) * direction
		}

		return String(firstValue).localeCompare(String(secondValue), 'pl') * direction
	})
}

const getPlacowkiBySearchText = async (searchText, placowkaId) => {
	if (placowkaId) {
		const placowka = await getPlacowkaDetails(placowkaId)
		if (!placowka || !doesPlacowkaMatchSearchText(placowka, searchText)) return []

		return [placowka]
	}

	const result = await PlacowkaService.search(searchText, {
		limit: PLACOWKI_SEARCH_LIMIT,
		sortColumn: 'nazwa',
		sortDirection: 'asc',
	})
	const placowki = result?.error || !Array.isArray(result?.data) ? [] : result.data

	savePlacowkiInCache(placowki)
	return placowki
}

const filterRozmowyByPlacowkaIds = (rows, placowkaIds) => {
	if (!placowkaIds || placowkaIds.size === 0) return []

	return rows.filter(rozmowa => placowkaIds.has(String(getRelationId(getPlacowkaValue(rozmowa)))))
}

const getRozmowyByPlacowkaName = async (searchText, options) => {
	if (!searchText) return []

	const placowki = await getPlacowkiBySearchText(searchText, options.filters?.id_placowka)
	const placowkaIds = new Set(placowki.map(placowka => String(getRelationId(placowka))).filter(Boolean))

	if (placowkaIds.size === 0) return []

	const result = await RozmowaService.getAll({
		limit: ROZMOWY_SEARCH_LIMIT,
		sortColumn: options.sortColumn,
		sortDirection: options.sortDirection,
		filters: options.filters,
	})
	const rows = result?.error || !Array.isArray(result?.data) ? [] : result.data

	return mergeRozmowyWithCachedPlacowki(filterRozmowyByPlacowkaIds(rows, placowkaIds))
}

export default function useRozmowyData() {
	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [filters, setFilters] = useState({})
	const [isPlacowkaFilterOpen, setIsPlacowkaFilterOpen] = useState(false)
	const [isDigRozmowyFilterOpen, setIsDigRozmowyFilterOpen] = useState(false)
	const [isDigNastepnejRozmowyFilterOpen, setIsDigNastepnejRozmowyFilterOpen] = useState(false)
	const [isSalesOpportunityFilterOpen, setIsSalesOpportunityFilterOpen] = useState(false)
	const [selectedPlacowkaId, setSelectedPlacowkaId] = useState(null)
	const [sortColumn, setSortColumn] = useState('dig_rozmowy')
	const [sortDirection, setSortDirection] = useState('desc')
	const [limit, setLimit] = useState(50)
	const activeRequestKeyRef = useRef('')

	const loadData = useCallback(async () => {
		const requestFilters = getRozmowaFilters(filters)
		const searchText = (filters.searchText || '').trim()
		const requestKey = JSON.stringify({
			searchText,
			limit,
			sortColumn,
			sortDirection,
			filters: requestFilters,
		})

		activeRequestKeyRef.current = requestKey
		setLoadingMore(prevLoadingMore => {
			if (!prevLoadingMore) setLoading(true)
			return prevLoadingMore
		})
		setError(null)

		try {
			const requestOptions = {
				limit,
				sortColumn,
				sortDirection,
				filters: requestFilters,
			}
			const [result, placowkaRows] = searchText
				? await Promise.all([
					RozmowaService.search(searchText, requestOptions),
					getRozmowyByPlacowkaName(searchText, requestOptions),
				])
				: [await RozmowaService.getAll(requestOptions), []]

			if (activeRequestKeyRef.current !== requestKey) return

			if (result.error && placowkaRows.length === 0) {
				setError(result.error)
				setData([])
				setTotalCount(0)
				setHasMore(false)
				return
			}

			const resultRows = result.error || !Array.isArray(result.data) ? [] : result.data
			const mergedRows = searchText
				? sortRozmowyRows(mergeRozmowyRows(resultRows, placowkaRows), sortColumn, sortDirection)
				: resultRows
			const rows = searchText ? mergedRows.slice(0, limit) : mergedRows
			const cachedRows = mergeRozmowyWithCachedPlacowki(rows)
			const resultTotalCount = result.error ? 0 : result.totalCount
			const resultHasMore = result.error ? false : result.hasMore
			const totalRowsCount = searchText ? Math.max(resultTotalCount, mergedRows.length) : resultTotalCount
			const hasSearchMore = searchText ? resultHasMore || mergedRows.length > rows.length : resultHasMore

			setData(cachedRows)
			setTotalCount(totalRowsCount)
			setHasMore(hasSearchMore)
			setLoading(false)
			setLoadingMore(false)

			enrichRozmowyWithPlacowki(rows).then(enrichedRows => {
				if (activeRequestKeyRef.current !== requestKey) return

				setData(enrichedRows)
				setTotalCount(totalRowsCount)
				setHasMore(hasSearchMore)
			}).catch(() => {})
		} catch (error) {
			if (activeRequestKeyRef.current !== requestKey) return

			setError(error.message || 'Failed to fetch rozmowy')
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} finally {
			if (activeRequestKeyRef.current === requestKey) {
				setLoading(false)
				setLoadingMore(false)
			}
		}
	}, [limit, sortColumn, sortDirection, filters])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMore) {
			setLimit(prev => prev + 50)
			setLoadingMore(true)
		}
	}, [hasMore, loadingMore])

	const setSorting = useCallback((column, direction) => {
		setSortColumn(column)
		setSortDirection(direction)
	}, [])

	const updateFilters = useCallback(newFilters => {
		setFilters(prevFilters => ({ ...prevFilters, ...newFilters }))
		setLimit(50)
	}, [])

	const handleSearchChange = useCallback(event => {
		updateFilters({ searchText: event.target.value })
	}, [updateFilters])

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}, [setSorting, sortColumn, sortDirection])

	const handleApplyPlacowkaFilter = useCallback(placowkaId => {
		updateFilters({ id_placowka: placowkaId || null })
		setSelectedPlacowkaId(placowkaId || null)
		setIsPlacowkaFilterOpen(false)
	}, [updateFilters])

	const handleApplyDigRozmowyFilter = useCallback((fromDate, toDate) => {
		updateFilters({
			dig_from_date: fromDate,
			dig_to_date: toDate,
		})
		setIsDigRozmowyFilterOpen(false)
	}, [updateFilters])

	const handleApplyDigNastepnejRozmowyFilter = useCallback((fromDate, toDate) => {
		updateFilters({
			dig_nastepnej_rozmowy_from_date: fromDate,
			dig_nastepnej_rozmowy_to_date: toDate,
		})
		setIsDigNastepnejRozmowyFilterOpen(false)
	}, [updateFilters])

	const handleApplySalesOpportunityFilter = useCallback(value => {
		const nextValue =
			value !== null && value !== undefined && value !== '' ? String(value) : null
		updateFilters({ szansa: nextValue })
		setIsSalesOpportunityFilterOpen(false)
	}, [updateFilters])

	const clearError = useCallback(() => {
		setError(null)
	}, [])

	return {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		handleSearchChange,
		handleSort,
		handleApplyPlacowkaFilter,
		handleApplyDigRozmowyFilter,
		handleApplyDigNastepnejRozmowyFilter,
		handleApplySalesOpportunityFilter,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		searchQuery: filters.searchText || '',
		isPlacowkaFilterOpen,
		isDigRozmowyFilterOpen,
		isDigNastepnejRozmowyFilterOpen,
		isSalesOpportunityFilterOpen,
		selectedPlacowkaId,
		digFromDate: filters.dig_from_date || '',
		digToDate: filters.dig_to_date || '',
		digNastepnejRozmowyFromDate: filters.dig_nastepnej_rozmowy_from_date || '',
		digNastepnejRozmowyToDate: filters.dig_nastepnej_rozmowy_to_date || '',
		hasDigRozmowyFilter: !!(filters.dig_from_date || filters.dig_to_date),
		hasDigNastepnejRozmowyFilter: !!(
			filters.dig_nastepnej_rozmowy_from_date || filters.dig_nastepnej_rozmowy_to_date
		),
		hasSalesOpportunityFilter:
			filters.szansa !== null &&
			filters.szansa !== undefined &&
			filters.szansa !== '',
		selectedSalesOpportunity: filters.szansa ?? null,
		setIsPlacowkaFilterOpen,
		setIsDigRozmowyFilterOpen,
		setIsDigNastepnejRozmowyFilterOpen,
		setIsSalesOpportunityFilterOpen,
		clearError,
	}
}
